import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Live still for a scrubber: one active decode, and only the latest pending
 * request is kept while it runs. Results arriving after unmount are dropped.
 * `extractFrame` should be referentially stable per video source.
 */
export function useLatestFrameStill(
  extractFrame: (time: number) => Promise<string | null>,
  time: number,
  enabled: boolean,
): { still: string | null; loading: boolean; clear: () => void } {
  const [still, setStill] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const pendingRef = useRef<{ time: number; extract: typeof extractFrame } | null>(null);
  const extractingRef = useRef(false);
  const mountedRef = useRef(true);
  // Re-arm on every mount: StrictMode runs mount -> cleanup -> mount.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    const request = { time, extract: extractFrame };
    pendingRef.current = request;
    setLoading(true);
    if (!extractingRef.current) {
      extractingRef.current = true;
      void (async () => {
        try {
          while (pendingRef.current) {
            const next = pendingRef.current;
            const result = await next.extract(next.time).catch(() => null);
            if (!mountedRef.current) return;
            if (pendingRef.current === next) {
              pendingRef.current = null;
              setStill(result);
              setLoading(false);
            }
          }
        } finally {
          extractingRef.current = false;
        }
      })();
    }
    return () => {
      if (pendingRef.current === request) pendingRef.current = null;
    };
  }, [extractFrame, time, enabled]);

  const clear = useCallback(() => setStill(null), []);
  return { still, loading, clear };
}
